import { useState, useEffect } from "react";
import { useAuth } from "~/lib/auth-context";
import {
  useRecipeMetadataForRecipe,
  useRecipeMetadata,
} from "~/lib/recipe-metadata-context";
import { useFetcher } from "@remix-run/react";

interface RatingStarsProps {
  recipeId: string;
  initialRating?: number;
  userRating?: number;
  readOnly?: boolean;
  onRatingChange?: (rating: number) => void;
  size?: "sm" | "md" | "lg";
  showCount?: boolean;
  animate?: boolean;
  deferSubmission?: boolean; // 즉시 API 호출을 하지 않는 모드
}

export default function RatingStars({
  recipeId,
  initialRating = 0,
  userRating,
  readOnly = false,
  onRatingChange,
  size = "md",
  showCount = false,
  animate = false,
  deferSubmission = false,
}: RatingStarsProps) {
  const { user } = useAuth();
  const { updateRating } = useRecipeMetadata();
  const { rating: ratingInfo } = useRecipeMetadataForRecipe(recipeId);

  const [rating, setRating] = useState<number>(
    userRating || ratingInfo.userRating || 0
  );
  const [hoveredRating, setHoveredRating] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [animateStars, setAnimateStars] = useState<boolean>(false);
  const fetcher = useFetcher();

  // 별 크기 설정
  const starSizeClass = {
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
  }[size];

  // 컨텍스트에서 가져온 평점 정보
  const averageRating = ratingInfo.average || initialRating;
  const ratingCount = ratingInfo.count || 0;

  // 사용자 평점이 변경되면 로컬 상태 업데이트
  useEffect(() => {
    if (ratingInfo.userRating !== null) {
      setRating(ratingInfo.userRating);
    }
  }, [ratingInfo.userRating]);

  // Fetcher 응답 처리
  useEffect(() => {
    if (fetcher.data && fetcher.state === "idle") {
      // 평점 제출 성공 시 컨텍스트 업데이트
      const data = fetcher.data as any;
      if (data?.success && data?.updatedRating) {
        updateRating(
          recipeId,
          data.updatedRating.rating,
          data.updatedRating.newAverage,
          data.updatedRating.newCount
        );
      }
    }
  }, [fetcher.data, fetcher.state, recipeId, updateRating]);

  // 애니메이션 효과
  useEffect(() => {
    if (animate && rating > 0) {
      setAnimateStars(true);
      const timer = setTimeout(() => {
        setAnimateStars(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [rating, animate]);

  // 별 클릭 핸들러
  const handleStarClick = async (selectedRating: number) => {
    if (readOnly || isSubmitting || !user) {
      return;
    }

    try {
      setIsSubmitting(true);

      // 이미 같은 평점을 선택한 경우 평점 취소
      const newRating = rating === selectedRating ? 0 : selectedRating;
      setRating(newRating);

      // 부모 컴포넌트에 변경 알림
      if (onRatingChange) {
        onRatingChange(newRating);
      }

      // deferSubmission이 true이면 API 호출하지 않음
      if (deferSubmission) {
        setIsSubmitting(false);
        return;
      }

      // API 호출로 평점 저장 (fetcher 사용)
      if (newRating > 0) {
        const formData = new FormData();
        formData.append("recipeId", recipeId);
        formData.append("rating", newRating.toString());

        fetcher.submit(formData, {
          method: "POST",
          action: "/api/ratings",
        });
      } else {
        // 평점 삭제
        fetcher.submit(
          { recipeId },
          {
            method: "DELETE",
            action: `/api/ratings?recipeId=${recipeId}`,
          }
        );
      }

      // 평점 정보 업데이트 (애니메이션 효과 추가)
      if (animate) {
        setAnimateStars(true);
        setTimeout(() => setAnimateStars(false), 1000);
      }
    } catch (error) {
      console.error("평점 저장 오류:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 별 렌더링 함수
  const renderStar = (starPosition: number) => {
    // 현재 표시할 별 상태 결정
    const displayRating = hoveredRating || rating || initialRating;
    const isFilled = starPosition <= displayRating;
    const isHalfFilled =
      !isFilled &&
      starPosition === Math.ceil(displayRating) &&
      displayRating % 1 !== 0;

    return (
      <button
        key={starPosition}
        type="button"
        disabled={readOnly || !user}
        className={`focus:outline-none min-w-2 min-h-2 mr-3 ${
          readOnly || !user ? "cursor-default" : "cursor-pointer"
        } ${animateStars && isFilled ? "animate-pulse" : ""}`}
        onClick={() => handleStarClick(starPosition)}
        onMouseEnter={() => !readOnly && setHoveredRating(starPosition)}
        onMouseLeave={() => !readOnly && setHoveredRating(0)}
        aria-label={`${starPosition}점`}
      >
        <svg
          className={`${starSizeClass} ${
            isFilled
              ? "text-yellow-400"
              : isHalfFilled
              ? "text-yellow-400"
              : "text-gray-300"
          } ${
            !readOnly && !isSubmitting && user ? "hover:text-yellow-400" : ""
          } transition-colors duration-200`}
          fill={isFilled ? "currentColor" : "none"}
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
          />
        </svg>
      </button>
    );
  };

  // 로그인 안내 메시지
  const loginPrompt = !user && !readOnly && (
    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
      <a href="/login" className="text-blue-500 hover:underline">
        로그인
      </a>
      하여 평가하기
    </span>
  );

  // 평점 카운트 표시
  const ratingCountDisplay = showCount && (
    <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
      {averageRating > 0 ? `${averageRating.toFixed(1)} / 5` : "아직 평가 없음"}
      {ratingCount > 0 && ` (${ratingCount}개의 평가)`}
    </span>
  );

  // 제출 상태 표시
  const submissionStatus = isSubmitting && (
    <span className="ml-2 text-sm text-blue-500 dark:text-blue-400">
      저장 중...
    </span>
  );

  // 제출 결과 표시
  const submissionResult =
    fetcher.data && !isSubmitting ? (
      <span
        className={`ml-2 text-sm ${
          fetcher.data &&
          typeof fetcher.data === "object" &&
          "success" in fetcher.data &&
          fetcher.data.success
            ? "text-green-500"
            : "text-red-500"
        } transition-opacity duration-500`}
      >
        {fetcher.data &&
        typeof fetcher.data === "object" &&
        "success" in fetcher.data
          ? fetcher.data.success
            ? "평가가 저장되었습니다"
            : "오류가 발생했습니다"
          : "오류가 발생했습니다"}
      </span>
    ) : null;

  return (
    <div className="flex">
      <div className="flex">{[1, 2, 3, 4, 5].map(renderStar)}</div>
      {loginPrompt}
      {ratingCountDisplay}
      {submissionStatus}
      {submissionResult}
    </div>
  );
}
